<template>
  <v-dialog :value="value" max-width="720" @input="$emit('input', $event)">
    <v-card>
      <v-card-title class="text-h6">{{ plan ? 'Editar plan a medida' : 'Nuevo plan a medida' }}</v-card-title>
      <v-card-text>
        <v-row dense class="mt-1">
          <v-col cols="12" sm="6">
            <v-select
              :value="start"
              :items="startOptions"
              label="Base"
              dense
              outlined
              hide-details
              @change="onStartChange"
            />
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="name"
              :placeholder="defaultName"
              label="Nombre del plan a medida"
              persistent-placeholder
              dense
              outlined
              hide-details
            />
          </v-col>
        </v-row>

        <p class="text-body-2 mt-4 mb-3">
          Agrega, quita o ajusta servicios. El precio es el de lista del plan base más lo que cuesta cada cambio (o menos lo
          que ahorra), con el margen objetivo del canal.
        </p>
        <v-simple-table dense>
          <thead>
            <tr>
              <th>Servicio</th>
              <th class="text-center" style="width: 110px">Eventos al año</th>
              <th class="text-right" style="width: 170px">Tope por evento ({{ currency }})</th>
              <th style="width: 48px"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in rows" :key="row.serviceCode">
              <td>{{ serviceName(row.serviceCode) }}</td>
              <td class="plan-customizer__events">
                <v-text-field
                  v-model.number="row.eventsPerYear"
                  type="number"
                  min="1"
                  dense
                  hide-details
                />
              </td>
              <td class="plan-customizer__cap">
                <v-text-field
                  v-model.number="row.cap"
                  type="number"
                  min="1"
                  :prefix="currencySymbol"
                  dense
                  hide-details
                />
              </td>
              <td>
                <v-btn icon small aria-label="Quitar servicio" @click="rows.splice(index, 1)">
                  <v-icon small>mdi-close</v-icon>
                </v-btn>
              </td>
            </tr>
          </tbody>
        </v-simple-table>

        <v-select
          v-model="serviceToAdd"
          :items="addableServices"
          item-text="name"
          item-value="code"
          label="Agregar servicio"
          class="mt-4"
          dense
          outlined
          hide-details
          @change="addService"
        />

        <template v-if="canSaveVariant">
          <v-divider class="my-4" />
          <div class="d-flex align-center">
            <v-text-field
              v-model="variantName"
              label="Guardar como variante del broker (opcional)"
              dense
              outlined
              hide-details
              class="mr-2"
            />
            <v-btn text color="primary" :disabled="!variantName.trim() || !hasChanges" @click="saveVariant">
              Guardar variante
            </v-btn>
          </div>
        </template>
        <v-divider class="my-4" />
        <div class="plan-customizer__live">
          <div class="pw-label mb-1">Precio de este plan</div>
          <p v-if="!pricer" class="text-body-2 mb-0">Ingresa la cantidad para ver el precio.</p>
          <p v-else-if="liveError" class="text-body-2 mb-0">{{ liveError }}</p>
          <div v-else class="d-flex flex-wrap plan-customizer__offers" :class="{ 'plan-customizer__stale': liveLoading }">
            <div v-for="offer in liveOffers" :key="offer.key" class="plan-customizer__offer">
              <div v-if="offer.label" class="text-caption">{{ offer.label }}</div>
              <div class="pw-kpi">{{ money(offer.price) }}</div>
              <div class="text-caption">
                {{ unit }} · {{ deltaText(offer.delta) }} vs {{ offer.baseLabel }}
              </div>
            </div>
            <v-progress-circular v-if="liveLoading && !liveOffers.length" indeterminate size="20" width="2" />
          </div>
        </div>
      </v-card-text>
      <v-card-actions>
        <v-btn text :disabled="!hasChanges" @click="reset">Deshacer cambios</v-btn>
        <v-spacer />
        <v-btn text @click="$emit('input', false)">Cancelar</v-btn>
        <v-btn color="primary" :disabled="!isDistinctPlan" @click="apply">{{ plan ? 'Guardar' : 'Agregar' }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { formatMoney, PLAN_OPTIONS } from '../utils/b2b-quotes.utils';
import { applyChanges } from '../utils/b2b-quote-form.utils';

const DEFAULT_START = 'plan:wellness';
const LIVE_DEBOUNCE_MS = 350;

/**
 * Creates or edits a custom plan of a quote: a standard plan or a broker
 * variant as starting point, a name, and its services. Gives back only the
 * changes against the starting point.
 */
export default {
  name: 'PlanCustomizerModal',
  props: {
    value: { type: Boolean, default: false },
    /** Custom plan being edited: { name, basePlan, partnerVariantId, changes }; null creates one. */
    plan: { type: Object, default: null },
    /** Standard plans of the parameters: [{ code, items }]. */
    plans: { type: Array, default: () => [] },
    /** Variants of the channel or broker: [{ id, name, basePlan, changes }]. */
    variants: { type: Array, default: () => [] },
    /** Reimbursable services of the country catalog: [{ code, name, cap }]. */
    services: { type: Array, default: () => [] },
    /** Plans a custom plan can start from (Employee Benefit leaves Virtual out). */
    basePlans: { type: Array, default: () => ['virtual', 'wellness', 'urgencias'] },
    /** Variants are saved per broker, so a direct quote cannot save one. */
    canSaveVariant: { type: Boolean, default: false },
    /**
     * Prices a candidate plan with the quote's current inputs: (plan) => Promise of
     * the preview response; null while the quote has no quantity to price.
     */
    pricer: { type: Function, default: null },
    currency: { type: String, default: 'CLP' },
    isAssistance: { type: Boolean, default: false }
  },
  data() {
    return {
      start: DEFAULT_START,
      name: '',
      rows: [],
      serviceToAdd: null,
      variantName: '',
      liveOffers: [],
      liveLoading: false,
      liveError: '',
      liveTimer: null,
      liveSeq: 0
    };
  },
  computed: {
    /** Symbol shown before a cap: S/ for soles, $ for pesos. */
    currencySymbol() {
      return this.currency === 'PEN' ? 'S/' : '$';
    },
    unit() {
      return this.isAssistance ? 'por asegurado' : 'por membresía';
    },
    startOptions() {
      const standard = PLAN_OPTIONS.filter((p) => this.basePlans.includes(p.value)).map((p) => ({
        value: `plan:${p.value}`,
        text: p.text
      }));
      const variants = this.variants.map((v) => ({ value: `variant:${v.id}`, text: `Variante: ${v.name}` }));
      return [...standard, ...variants];
    },
    startVariant() {
      if (!this.start.startsWith('variant:')) return null;
      return this.variants.find((v) => `variant:${v.id}` === this.start) ?? null;
    },
    startPlan() {
      return this.startVariant?.basePlan ?? this.start.replace('plan:', '');
    },
    defaultName() {
      if (this.startVariant) return this.startVariant.name;
      const label = PLAN_OPTIONS.find((p) => p.value === this.startPlan)?.text ?? '';
      return `${label} a medida`;
    },
    /** Services of the starting point: the standard plan with the variant applied. */
    baseItems() {
      const plan = this.plans.find((p) => p.code === this.startPlan);
      return applyChanges(plan?.items ?? [], this.startVariant?.changes ?? []);
    },
    baseRows() {
      return this.baseItems.map((item) => ({
        serviceCode: item.serviceCode,
        eventsPerYear: item.eventsPerYear,
        cap: item.cap ?? this.catalogCap(item.serviceCode)
      }));
    },
    addableServices() {
      const used = new Set(this.rows.map((r) => r.serviceCode));
      return this.services.filter((s) => !used.has(s.code));
    },
    hasChanges() {
      return this.diff().length > 0;
    },
    /** A custom plan must differ from the standard one: a variant or at least one change. */
    isDistinctPlan() {
      return Boolean(this.startVariant) || this.hasChanges;
    }
  },
  watch: {
    value(open) {
      if (open) this.load();
    },
    rows: {
      deep: true,
      handler() {
        if (this.value) this.scheduleLive();
      }
    },
    start() {
      if (this.value) this.scheduleLive();
    }
  },
  beforeDestroy() {
    clearTimeout(this.liveTimer);
  },
  methods: {
    candidate() {
      return {
        name: this.name.trim() || this.defaultName,
        basePlan: this.startPlan,
        partnerVariantId: this.startVariant?.id ?? null,
        changes: this.diff()
      };
    },
    scheduleLive() {
      clearTimeout(this.liveTimer);
      this.liveTimer = setTimeout(this.priceLive, LIVE_DEBOUNCE_MS);
    },
    /** Prices the plan as it is being edited, next to its base plan, for every offer of the quote. */
    async priceLive() {
      if (!this.pricer) return;
      const seq = ++this.liveSeq;
      this.liveLoading = true;
      const res = await this.pricer(this.candidate());
      // A slower, older answer must not overwrite a newer one.
      if (seq !== this.liveSeq) return;
      this.liveLoading = false;
      if (!res || res.status !== 'ok') {
        this.liveError = 'No se pudo calcular el precio con estos servicios.';
        this.liveOffers = [];
        return;
      }
      this.liveError = '';
      this.liveOffers = this.offersOf(res.data.lines);
    },
    /** Lines come in pairs per offer: the base plan, then the candidate. */
    offersOf(lines) {
      const modalities = [...new Set(lines.map((l) => l.modality))];
      return modalities.map((modality) => {
        const [base, plan] = lines.filter((l) => l.modality === modality);
        const priceOf = (l) => (this.isAssistance ? l.perInsured : l.netAfterDiscount);
        return {
          key: modality,
          // Asistencias has a single offer whose unit already shows under the price.
          label: modality === 'pool' ? 'Flexible (bolsa)' : this.isAssistance ? null : 'Colectivo',
          price: priceOf(plan),
          delta: priceOf(plan) - priceOf(base),
          baseLabel: base.label
        };
      });
    },
    money(value) {
      return formatMoney(value, this.currency);
    },
    deltaText(delta) {
      if (Math.abs(delta) < 0.5) return 'igual';
      return `${delta > 0 ? '+' : '−'}${this.money(Math.abs(delta))}`;
    },
    catalogCap(code) {
      return this.services.find((s) => s.code === code)?.cap ?? null;
    },
    serviceName(code) {
      return this.services.find((s) => s.code === code)?.name ?? code;
    },
    load() {
      this.liveOffers = [];
      this.liveError = '';
      const plan = this.plan;
      this.start = plan ? (plan.partnerVariantId ? `variant:${plan.partnerVariantId}` : `plan:${plan.basePlan}`) : DEFAULT_START;
      this.name = plan?.name ?? '';
      this.rows = this.rowsWith(plan?.changes ?? []);
      this.variantName = '';
      this.scheduleLive();
    },
    rowsWith(changes) {
      return applyChanges(this.baseRows, changes).map((row) => ({
        ...row,
        cap: row.cap ?? this.catalogCap(row.serviceCode)
      }));
    },
    onStartChange(start) {
      this.start = start;
      this.rows = this.rowsWith([]);
    },
    addService(code) {
      if (!code) return;
      this.rows.push({ serviceCode: code, eventsPerYear: 1, cap: this.catalogCap(code) });
      this.$nextTick(() => {
        this.serviceToAdd = null;
      });
    },
    reset() {
      this.rows = this.rowsWith([]);
    },
    /** Changes of the edited rows against the starting point. */
    diff() {
      const changes = [];
      for (const row of this.rows) {
        const base = this.baseRows.find((b) => b.serviceCode === row.serviceCode);
        const events = Number(row.eventsPerYear) || 1;
        const cap = Number(row.cap) || null;
        if (!base || base.eventsPerYear !== events || base.cap !== cap) {
          changes.push({ serviceCode: row.serviceCode, eventsPerYear: events, ...(cap ? { cap } : {}) });
        }
      }
      for (const base of this.baseRows) {
        if (!this.rows.some((r) => r.serviceCode === base.serviceCode)) {
          changes.push({ serviceCode: base.serviceCode, eventsPerYear: 0 });
        }
      }
      return changes;
    },
    apply() {
      // payload: the custom plan, with its changes against the starting point
      this.$emit('apply', this.candidate());
      this.$emit('input', false);
    },
    saveVariant() {
      // payload: { name, basePlan, changes } — changes against the standard plan, for the broker
      this.$emit('save-variant', {
        name: this.variantName.trim(),
        basePlan: this.startPlan,
        changes: [...(this.startVariant?.changes ?? []), ...this.diff()]
      });
    }
  }
};
</script>

<style scoped>
.plan-customizer__events ::v-deep input {
  text-align: center;
}
.plan-customizer__cap ::v-deep input {
  text-align: right;
  font-variant-numeric: tabular-nums;
}
.plan-customizer__offers {
  gap: 24px;
}
.plan-customizer__stale {
  opacity: 0.6;
}
</style>
